import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_BADGE_DANGER_CLASSNAME,
  ADMIN_BADGE_INFO_CLASSNAME,
  ADMIN_BADGE_NEUTRAL_CLASSNAME,
  ADMIN_BADGE_SUCCESS_CLASSNAME,
  ADMIN_PAGE_CLASSNAME,
  ADMIN_STATS_ROW_CLASSNAME,
  ADMIN_STAT_CARD_CLASSNAME,
  ADMIN_STAT_LABEL_CLASSNAME,
  ADMIN_STAT_VALUE_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

interface Export {
  id: string;
  userId: string;
  userEmail?: string;
  documentId: string | null;
  documentTitle?: string;
  exportType: 'pdf' | 'word' | 'docx' | 'epub';
  createdAt: string;
}

export const Exports: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const [exports, setExports] = useState<Export[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExports = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/exports?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/exports`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch exports');
        }

        const data = await response.json();
        setExports(data.exports || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching exports:', err);
        setError('Error al cargar exportaciones');
      } finally {
        setLoading(false);
      }
    };

    fetchExports();
  }, [token, user, masterKey]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  if (loading) {
    return <AdminLoadingState />;
  }

  const pdfCount = exports.filter((e) => e.exportType === 'pdf').length;
  const wordCount = exports.filter((e) => e.exportType === 'word' || e.exportType === 'docx').length;
  const epubCount = exports.filter((e) => e.exportType === 'epub').length;
  const totalCount = exports.length;

  const getExportTypeLabel = (type: string): string => {
    switch (type) {
      case 'pdf':
        return 'PDF';
      case 'word':
      case 'docx':
        return 'Word';
      case 'epub':
        return 'EPUB';
      default:
        return type;
    }
  };

  const getExportTypeBadgeClassName = (type: string): string => {
    switch (type) {
      case 'pdf':
        return ADMIN_BADGE_DANGER_CLASSNAME;
      case 'word':
      case 'docx':
        return ADMIN_BADGE_INFO_CLASSNAME;
      case 'epub':
        return ADMIN_BADGE_SUCCESS_CLASSNAME;
      default:
        return ADMIN_BADGE_NEUTRAL_CLASSNAME;
    }
  };

  return (
    <>
      <div className={ADMIN_PAGE_CLASSNAME}>
        {error && (
          <div className="mb-8">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        <div className={ADMIN_STATS_ROW_CLASSNAME}>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>Total</div>
            <div className={ADMIN_STAT_VALUE_CLASSNAME}>{totalCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>PDF</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-red-600 dark:text-red-400`}>{pdfCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>Word</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-blue-600 dark:text-blue-400`}>{wordCount}</div>
          </div>
          <div className={ADMIN_STAT_CARD_CLASSNAME}>
            <div className={`${ADMIN_STAT_LABEL_CLASSNAME} mb-2`}>EPUB</div>
            <div className={`${ADMIN_STAT_VALUE_CLASSNAME} text-green-700 dark:text-green-400`}>{epubCount}</div>
          </div>
        </div>

        {exports.length === 0 ? (
          <AdminEmptyState>No hay exportaciones registradas</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[1000px]">
              <thead>
                <tr>
                  <th>
                    Usuario
                  </th>
                  <th>
                    Proyecto
                  </th>
                  <th>
                    Título del Proyecto
                  </th>
                  <th>
                    Tipo
                  </th>
                  <th>
                    Fecha
                  </th>
                </tr>
              </thead>
              <tbody>
                {exports.map((e) => (
                  <tr
                    key={e.id}
                  >
                    <td>
                      {e.userEmail ? (
                        <Link
                          to={`/admin/users/${e.userId}`}
                          className="admin-table-link"
                        >
                          {e.userEmail}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {e.documentId ? (
                        <Link
                          to={`/documents/${e.documentId}`}
                          className="admin-table-link"
                        >
                          {e.documentId.substring(0, 8)}...
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="max-w-[300px]">
                      <div className="truncate" title={e.documentTitle || '-'}>
                        {e.documentTitle || '-'}
                      </div>
                    </td>
                    <td>
                      <span className={getExportTypeBadgeClassName(e.exportType)}>
                        {getExportTypeLabel(e.exportType)}
                      </span>
                    </td>
                    <td className="admin-table-cell-muted">
                      {new Date(e.createdAt).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}
      </div>
    </>
  );
};
